import {useFx, useSize, color} from '../fx.js';
import * as THREE from 'three'
import {Background} from '../bg.js';
import React, { useMemo, useRef, useEffect, useState, useCallback } from 'react'
import RefractionMaterial from '../refraction-material'
import lerp from 'lerp'
const { WebGLRenderTarget, Object3D, Vector2, Vector3 } = THREE
const cameraFov = 1
const cameraZ = 100
const bgZ = -cameraZ*2
function Glass({layers = 1}) {
	const fx = useFx();
	const size = useSize()
	const {gl, scene, camera, frameRun} = fx
	const [model, setModel] = useState(false)
	const [backfaceFbo, setBackfaceFbo] = useState(false)
	const [backfaceMaterial, setBackfaceMaterial] = useState(false)
	const [envFbo, setEnvFbo] = useState(false)
	const [refractionMaterial, setRefractionMaterial] = useState(false)
	const lastTime = useRef(0);
	const rot = useRef({x: 0, y: 0, z: 0});
	const rotXYMove = useRef({x: 0, y: 0});
	const dMouseMove = useRef({x: 0, y: 0});
	const lastMouse = useRef({x: fx.mouse.x, y: fx.mouse.y});
	const lastMouseMove = useRef({x: fx.mouse.x, y: fx.mouse.y});
	useEffect(() => {
		const defines = {}
		if(fx.reflectionColor){
			defines.USE_FRESNEL = ''
		}
		const refractionMaterial = new RefractionMaterial({
			defines,
			uniforms: {
				envMap: { value: null },
				backfaceMap: { value: null },
				resolution: { value: new Vector2(1, 1) },
				reflectionColor: { value: new Vector3(0,0,0) },
				fresnelRange: { value: 0.95 }
			},
		})
		setRefractionMaterial(refractionMaterial)
		return () => {
			refractionMaterial.dispose()
		}
	}, [fx.reflectionColor]);
	useEffect(() => {
		const backfaceMaterial = new RefractionMaterial({
			defines: {
				IS_BACKFACE: ''
			},
			side: THREE.BackSide
		})
		setBackfaceMaterial(backfaceMaterial)
		return () => {
			backfaceMaterial.dispose()
		}
	}, []);
	useEffect(() => {
		if(refractionMaterial){
			const geom = new THREE.IcosahedronGeometry(2)
			const model = new THREE.Mesh(geom, refractionMaterial)
			model.layers.set(layers)
			scene.add(model)
			setModel(model)
			return () => {
				scene.remove(model)
				geom.dispose()
			}
		}
	}, [layers, scene, refractionMaterial]);
	useEffect(() => {
		const w = size.width;
		const h = size.height;
		const envFbo = new WebGLRenderTarget(w, h)
		const backfaceFbo = new WebGLRenderTarget(w, h)
		setEnvFbo(envFbo)
		setBackfaceFbo(backfaceFbo)
		return () => {
			envFbo.dispose()
			backfaceFbo.dispose()
		}
	}, [size.width, size.height]);
	useEffect(() => {
		if(backfaceFbo && refractionMaterial){
			refractionMaterial.uniforms.backfaceMap.value = backfaceFbo.texture;
		}
	}, [refractionMaterial, backfaceFbo]);
	useEffect(() => {
		if(refractionMaterial && envFbo){
			refractionMaterial.uniforms.envMap.value = envFbo.texture
		}
	}, [refractionMaterial, envFbo]);
	useEffect(() => {
		if(refractionMaterial && fx.reflectionColor){
			refractionMaterial.uniforms.reflectionColor.value = color(fx.reflectionColor)
		}
	}, [fx.reflectionColor, refractionMaterial]);
	useEffect(() => {
		const w = size.width;
		const h = size.height;
		if(refractionMaterial){
			refractionMaterial.uniforms.resolution.value.x = w;
			refractionMaterial.uniforms.resolution.value.y = h;
		}
	}, [size.width, size.height, refractionMaterial])
	useEffect(()=>{
		if(model && envFbo && backfaceFbo && refractionMaterial){
			return frameRun((t)=>{
				const dt = t - lastTime.current;
				lastTime.current = t;

				const dr = (dt > 1 ? 1 : dt) / 32;

				const mouseMoveX = lerp(lastMouseMove.current.x, fx.mouse.x, 0.1);
				const mouseMoveY = lerp(lastMouseMove.current.y, fx.mouse.y, 0.1);

				const dMouseX = lastMouse.current.x - fx.mouse.x;
				const dMouseY = lastMouse.current.y - fx.mouse.y;

				if(dMouseX !==0 || Math.abs(fx.mouse.x - mouseMoveX) > 0.01){
					dMouseMove.current.y = lastMouseMove.current.y - mouseMoveY;
					rot.current.x += dMouseMove.current.y/2;
				}else if(dMouseMove.current.y < 0){
					rot.current.x -= dr;
				}else{
					rot.current.x += dr;
				}
				if(dMouseY !==0 || Math.abs(fx.mouse.y - mouseMoveY) > 0.01){
					dMouseMove.current.x = lastMouseMove.current.x - mouseMoveX;
					rot.current.y -= dMouseMove.current.x/2;
				}else if(dMouseMove.current.x > 0){
					rot.current.y -= dr;
				}else{
					rot.current.y += dr;
				}
				rot.current.z += dr;
				rotXYMove.current.x = lerp(rotXYMove.current.x, rot.current.x, 0.1);
				rotXYMove.current.y = lerp(rotXYMove.current.y, rot.current.y, 0.1);
				model.rotation.set(rotXYMove.current.x, rotXYMove.current.y, rot.current.z)
				lastMouseMove.current.x = mouseMoveX;
				lastMouseMove.current.y = mouseMoveY;
				lastMouse.current.x = fx.mouse.x;
				lastMouse.current.y = fx.mouse.y;

				camera.layers.set(0)
				gl.setRenderTarget(envFbo)
				gl.render(scene, camera)
				camera.layers.set(layers)
				model.material = backfaceMaterial
				gl.setRenderTarget(backfaceFbo)
				gl.render(scene, camera)

				camera.layers.enable(0)
				gl.setRenderTarget(null)
				model.material = refractionMaterial
			})
		}
	}, [frameRun, model, envFbo, backfaceFbo, backfaceMaterial, refractionMaterial, fx, camera, gl, layers, scene])
	return null
}
const bgPosition = new Vector3(0,0,bgZ)
function App(){
	const fx = useFx();
	const bgReady = useCallback(()=>{
		fx.ready()
	},[fx])
	return(
		<>
			<Background position={bgPosition} readyFun={bgReady} />
			<Glass layers={1} />
		</>
	)
}
const EffectRefraction = {
	App,
	config: {
		createCamera: ()=>{
			const cam = new THREE.PerspectiveCamera(cameraFov, 1, 0.1, 1000)
			cam.position.z = cameraZ
			cam.lookAt(0, 0, 0)
			return cam
		}
	}
}
export {EffectRefraction, Glass};
